import { LitElement, html, TemplateResult } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { SliderEvents, SliderRange, SliderTranslateStrings } from '../types';
import { GzInputNumberWebComponent } from '../../gzInput';
import { getRgbByValue } from '../../../utils/htmlElement.utils';
import { isEmpty } from '@nirvana/shared/basic-types/object';

export class GzSliderWebComponent extends LitElement {
  // see https://lit-element.polymer-project.org/guide/styles
  static get styles() {
    return styles;
  }
  // see https://lit-element.polymer-project.org/guide/properties

  @property({ type: Object, reflect: true })
  translateStrings: SliderTranslateStrings = {
    fromLabel: 'From',
    toLabel: 'To',
  };

  //default value for the min slider thumb
  @property({ type: Number, attribute: true, reflect: true })
  valueMin = 0;

  //default value for the max slider thumb
  @property({ type: Number, attribute: true, reflect: true })
  valueMax = 0;

  //config for the slider
  @property({
    type: Object,
    attribute: true,
    reflect: false,
  })
  private sliderRange: SliderRange = {
    min: 0,
    max: 100,
    step: 1,
  };

  private rangeGap = 5;
  private thumbDiameter = 20;

  //this is used to verify if the inputs has values so we can clear the colors
  @state()
  private dirty = false;

  @query('.input-min')
  private inputMin!: HTMLInputElement;

  @query('.min-input-field')
  private inputMinField!: GzInputNumberWebComponent;

  @query('.input-max')
  private inputMax!: HTMLInputElement;

  @query('.max-input-field')
  private inputMaxField!: GzInputNumberWebComponent;

  @query('.range-thumb')
  private thumb!: HTMLElement;

  @query('.slider-track')
  private track!: HTMLElement;

  updated(changedProps) {
    if (changedProps.has('valueMin') || changedProps.has('valueMax')) {
      this.updateSlider();
    }
  }

  render() {
    return html`
      <div class="wrapper">
        <div class="container">
          <div class="slider-track"></div>
          <div class="range-track" @click="${this.onTrackClick}"></div>
          <div class="range-track-mask-left" @click="${this.onTrackClick}"></div>
          <div class="range-track-mask-right" @click="${this.onTrackClick}"></div>
          ${this.renderSliders()} ${this.renderDatalist()}
        </div>
        ${this.renderInputs()}
      </div>
    `;
  }

  private getValueForSliderInput(value: number | undefined): number {
    if (isEmpty(value)) {
      return 0;
    } else {
      return value as number;
    }
  }

  private renderDatalist(): TemplateResult {
    const optionsCount = 11;
    return html`
      <div class="datalist">
        ${[...Array(optionsCount).keys()].map((item, index) => {
          const val = index === 0 ? index : index * 10;
          return html`<span value="${val}">${val}</option>`;
        })}
      </div>
    `;
  }

  private renderSliders(): TemplateResult {
    return html` <div class="slider-inputs">
      <input
        type="range"
        max=${this.sliderRange.max}
        min=${this.sliderRange.min}
        step=${this.sliderRange.step}
        .value=${this.getValueForSliderInput(this.valueMin).toString()}
        id="slider-1"
        class="input-min"
        list="tickmarks"
        multiple
        @input=${() => {
          this.inputChangeHandlerMin(this.inputMin.value);
        }}
      />
      <input
        type="range"
        max=${this.sliderRange.max}
        min=${this.sliderRange.min}
        step=${this.sliderRange.step}
        .value=${this.getValueForSliderInput(this.valueMax).toString()}
        id="slider-2"
        class="input-max"
        @input=${() => {
          this.inputChangeHandlerMax(this.inputMax.value);
        }}
      />
    </div>`;
  }

  private renderInputs(): TemplateResult {
    return html` <div class="input-container">
      <div class="d-flex flex-column">
        <label for="max-input-field">${this.translateStrings.fromLabel}</label>
        <gz-input-number
          .min=${this.dirty ? this.valueMin + this.rangeGap : 0}
          max="100"
          id="max-input-field"
          class="max-input-field"
          value=${this.getValueForSliderInput(this.valueMax)}
          @change=${() => {
            this.inputChangeHandlerMax(this.inputMaxField.value);
          }}
        >
        </gz-input-number>
      </div>
      <div class="d-flex flex-column ml-2">
        <label for="min-input-field">${this.translateStrings.toLabel}</label>
        <gz-input-number
          min="0"
          .max=${this.valueMax ? this.valueMax - this.rangeGap : 0}
          id="min-input-field"
          class="min-input-field"
          value=${this.getValueForSliderInput(this.valueMin)}
          @change=${() => {
            this.inputChangeHandlerMin(this.inputMinField.value);
          }}
        >
        </gz-input-number>
      </div>
    </div>`;
  }

  private sliderChangeEvent() {
    this.dispatchEvent(
      new CustomEvent(SliderEvents.CHANGE, {
        detail: {
          min: this.valueMin,
          max: this.valueMax,
        },
        bubbles: true,
        cancelable: true,
        composed: false,
      })
    );
  }

  private inputChangeHandlerMin(inputValue: any): void {
    this.valueMin = parseInt(inputValue, 10);

    if (this.valueMax - this.valueMin <= this.rangeGap) {
      this.inputMin.value = (this.valueMax - this.rangeGap).toString();
      this.valueMin = +this.inputMin.value;
      this.dirty = true;
    }
  }

  private inputChangeHandlerMax(inputValue: any): void {
    this.valueMax = parseInt(inputValue.toString(), 10);

    if (this.valueMax - this.valueMin <= this.rangeGap) {
      this.inputMax.value = (this.valueMin + this.rangeGap).toString();
      this.valueMax = +this.inputMax.value;
      this.dirty = true;
    }
  }

  private onTrackClick(event: any) {
    // we have to take into account thumb width when doing the computations
    const sliderRect = this.track.getBoundingClientRect();
    let x = event.clientX - sliderRect.left - this.thumbDiameter / 2;
    if (x < 0) {
      x = 0;
    }
    if (x > sliderRect.right - this.thumbDiameter / 2) {
      x = sliderRect.right - this.thumbDiameter / 2;
    }
    const percentage = 100 - (x * 100) / (sliderRect.right - sliderRect.left + 1 - this.thumbDiameter);
    const value = this.sliderRange.min + ((this.sliderRange.max - this.sliderRange.min) * percentage) / 100;

    // check if value is closer to valueMin or to valueMax
    if (Math.abs(value - this.valueMin) < Math.abs(value - this.valueMax)) {
      this.inputChangeHandlerMin(value);
    } else {
      this.inputChangeHandlerMax(value);
    }
  }

  private updateSlider(): void {
    if (this.valueMax || this.valueMin) {
      this.dirty = true;
    }

    if (!this.dirty) {
      this.resetColors();
      return;
    }

    const percentageMin = (this.valueMin - this.sliderRange.min) / (this.sliderRange.max - this.sliderRange.min);
    const percentageMax = (this.valueMax - this.sliderRange.min) / (this.sliderRange.max - this.sliderRange.min);
    const sliderValueMin = `${percentageMin * 100}%`;
    const sliderValueMax = `${percentageMax * 100}%`;
    const sliderMinValueColor = getRgbByValue(percentageMin * 100);
    const sliderMaxValueColor = getRgbByValue(percentageMax * 100);

    this.style.setProperty('--slider-second-value-max', sliderValueMax);
    this.style.setProperty('--slider-second-value-min', sliderValueMin);
    this.style.setProperty('--thumb-value-min-color', sliderMinValueColor);
    this.style.setProperty('--thumb-value-max-color', sliderMaxValueColor);

    this.sliderChangeEvent();
  }

  public clearSlider() {
    this.valueMin = 0;
    this.valueMax = 0;
    this.dirty = false;
  }

  private resetColors() {
    this.style.removeProperty('--slider-second-value-max');
    this.style.removeProperty('--slider-second-value-min');
    this.style.removeProperty('--thumb-value-min-color');
    this.style.removeProperty('--thumb-value-max-color');
    this.style.removeProperty('--slider-background');
  }
}
